import {
  adminAppVersionsApi,
  type AppVersionAdmin,
} from '@/api/admin/adminAppVersionsApi';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { handleGlobalError } from '@/utils/error-handler';
import { useCallback, useEffect, useState } from 'react';

const PlatformCard = ({
  version,
  onSaved,
}: {
  version: AppVersionAdmin;
  onSaved: () => void;
}) => {
  const { toast } = useToast();
  const [draft, setDraft] = useState<AppVersionAdmin>(version);
  const [confirmation, setConfirmation] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Raising minimumVersion is a kill switch: it locks out every user below that
  // number. We ask for confirmation on ANY change to the floor (there is no
  // semver comparison here) because typing the value by hand is the guard
  // against a stray click.
  const changesFloor = draft.minimumVersion !== version.minimumVersion;
  const canSave = !changesFloor || confirmation === draft.minimumVersion;

  const save = async () => {
    setIsSaving(true);
    try {
      // The confirmation guard compares the draft against the copy this card
      // loaded, and the POST below writes the whole record. If anything moved
      // since — another admin, another tab — that comparison is against a stale
      // baseline and the write would silently revert their change. Read before
      // writing, and hand the conflict back to the admin.
      const current = (await adminAppVersionsApi.list()).find(
        (candidate) => candidate.platform === version.platform,
      );

      if (
        current &&
        (current.latestVersion !== version.latestVersion ||
          current.minimumVersion !== version.minimumVersion ||
          current.updateMessage !== version.updateMessage ||
          current.storeUrl !== version.storeUrl)
      ) {
        // The inputs have to move too, or the toast describes something that did
        // not happen: `draft` is seeded from the prop once and never re-syncs, so
        // without this the card keeps showing the old edit while `onSaved()`
        // refreshes `version` underneath it. The next Guardar would then compare
        // the freshly reloaded `version` against an equally fresh `current`, find
        // no conflict, and POST the whole stale draft — reverting in silence the
        // very change this branch exists to protect. `confirmation` goes with it:
        // it was typed against a minimumVersion that may no longer be there.
        setDraft(current);
        setConfirmation('');
        toast({
          variant: 'destructive',
          title: 'Los datos cambiaron en el servidor',
          description:
            'Alguien actualizó esta plataforma desde otro lado. Recargamos lo que hay guardado: revisá tus cambios y volvé a guardar.',
        });
        onSaved();
        return;
      }

      await adminAppVersionsApi.update(draft);
      toast({ title: `Versiones de ${draft.platform} actualizadas` });
      setConfirmation('');
      onSaved();
    } catch (error) {
      handleGlobalError(error, 'No se pudo actualizar', 'Revisá los datos e intentá de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="space-y-3 p-4">
      <h2 className="font-semibold text-gray-900">{version.platform}</h2>

      <div className="space-y-1">
        <Label>Última versión</Label>
        <Input
          value={draft.latestVersion}
          onChange={(event) =>
            setDraft({ ...draft, latestVersion: event.target.value })
          }
        />
      </div>

      <div className="space-y-1">
        <Label>Versión mínima</Label>
        <Input
          value={draft.minimumVersion}
          onChange={(event) =>
            setDraft({ ...draft, minimumVersion: event.target.value })
          }
        />
      </div>

      <div className="space-y-1">
        <Label>Mensaje de actualización</Label>
        <Input
          value={draft.updateMessage ?? ''}
          onChange={(event) =>
            setDraft({ ...draft, updateMessage: event.target.value })
          }
        />
      </div>

      <div className="space-y-1">
        <Label>URL de la tienda</Label>
        <Input
          value={draft.storeUrl ?? ''}
          onChange={(event) => setDraft({ ...draft, storeUrl: event.target.value })}
        />
      </div>

      {changesFloor && (
        <div className="space-y-1 rounded-md border border-orange-200 bg-orange-50 p-3">
          <p className="text-sm text-orange-800">
            Vas a cambiar la versión mínima. Todos los usuarios con una versión
            anterior a <strong>{draft.minimumVersion}</strong> van a quedar
            obligados a actualizar. Escribí el número para confirmar.
          </p>
          <Input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            placeholder={draft.minimumVersion}
          />
        </div>
      )}

      <Button onClick={save} disabled={!canSave || isSaving}>
        {isSaving ? 'Guardando...' : 'Guardar'}
      </Button>
    </Card>
  );
};

const AdminVersionesPage = () => {
  const [versions, setVersions] = useState<AppVersionAdmin[]>([]);
  // Only the very first load blanks the screen. A refetch — after a save, or
  // after a detected conflict — must leave the cards mounted: unmounting them
  // destroys each card's local draft and confirmation along with whatever the
  // admin had typed but not saved yet.
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setVersions(await adminAppVersionsApi.list());
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudieron cargar las versiones',
        'Revisá la conexión e intentá de nuevo.',
      );
    } finally {
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-2xl font-semibold text-gray-900">Versiones de la app</h1>
      {isInitialLoading ? (
        <Skeleton className="h-48" />
      ) : versions.length === 0 ? (
        <p className="text-sm text-gray-500">No hay plataformas configuradas.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {versions.map((version) => (
            <PlatformCard
              key={version.platform}
              version={version}
              onSaved={load}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminVersionesPage;
